
import { useMemo } from "react";
import { MapPin } from "lucide-react";
import { Loader } from "../../Common/Loader";
import Breadcrumb from "components/shared/Breadcrumb/Breadcrumb";
import TableWithFilters from "components/shared/TableWithFilters/TableWithFilters";
import { DesktopPageTabsBar } from "components/shared/PageTabs/PageTabs";
import { TitleRow } from "components/core/Common/DesktopHelpers/DesktopHelpers";
import {
  DesktopPage,
  DesktopTitle,
  CellText,
  BidIdLink,
  StatusBadge,
  LocationCell,
  StateContainer,
  StateTitle,
  StateMessage,
  StateButton,
} from "./styles.bidsListDesktop";

const BID_TABS = [
  { key: "live",      label: "Live" },
  { key: "upcoming",  label: "Upcoming" },
  { key: "awarded",   label: "Awarded" },
  { key: "submitted", label: "Submitted" },
  { key: "previous",  label: "Previous" },
  { key: "all",       label: "All" },
];

const STATUS_LABEL = {
  live:          "Live",
  upcoming:      "Upcoming",
  awarded:       "Awarded",
  submitted:     "Submitted",
  not_awarded:   "Not Awarded",
  no_submission: "No Submission",
  completed:     "Completed",
  previous:      "Previous",
};

function BidsErrorState({ onRetry }) {
  return (
    <StateContainer>
      <StateTitle>Could not load bids</StateTitle>
      <StateMessage>Please check your connection and try again.</StateMessage>
      <StateButton type="button" onClick={onRetry}>
        Retry
      </StateButton>
    </StateContainer>
  );
}

function BidsEmptyState() {
  return (
    <StateContainer>
      <StateTitle>No bids found</StateTitle>
      <StateMessage>There are no bids available in this category right now.</StateMessage>
    </StateContainer>
  );
}

export default function BidsListDesktopView({
  activeTab,
  pageNumber,
  bidsTableRowData,
  hasNextPage,
  isInitialLoading,
  hasError,
  fetchBids,
  handleTabChange,
  goToPage,
  navigate,
}) {
  const columns = useMemo(
    () => [
      {
        id: "bid_id",
        header: "Bid ID",
        meta: { width: 180 },
        cell: (info) => (
          <BidIdLink
            onClick={() =>
              navigate(`/bids/details/${info.row.original.id}`, {
                state: { bidId: info.row.original.id, status: info.row.original.status },
              })
            }
          >
            #{info.row.original.bidId}
          </BidIdLink>
        ),
      },
      {
        id: "title",
        header: "Title",
        meta: { width: 240 },
        cell: (info) => <CellText>{info.row.original.title}</CellText>,
      },
      {
        id: "location",
        header: "Location",
        meta: { width: 180 },
        cell: (info) => (
          <LocationCell>
            <MapPin size={16} />
            <CellText>{info.row.original.location}</CellText>
          </LocationCell>
        ),
      },
      {
        id: "delivery_range",
        header: "Delivery range",
        meta: { width: 180 },
        cell: (info) => <CellText>{info.row.original.deliveryRange}</CellText>,
      },
      {
        id: "total_amount",
        header: "Amount",
        meta: { width: 140 },
        cell: (info) => <CellText>{info.row.original.totalAmount}</CellText>,
      },
      {
        id: "products",
        header: "Products",
        meta: { width: 100 },
        cell: (info) => (
          <CellText>
            {info.row.original.productCount || info.row.original.products?.length || 0} SKUs
          </CellText>
        ),
      },
      {
        id: "status",
        header: "Status",
        meta: { width: 150 },
        cell: (info) => (
          <StatusBadge $status={info.row.original.status}>
            {STATUS_LABEL[info.row.original.status] || info.row.original.status}
          </StatusBadge>
        ),
      },
    ],
    [navigate]
  );

  return (
    <DesktopPage>
      <Breadcrumb items={[{ label: "Home" }, { label: "Bids" }]} />
      <TitleRow>
        <DesktopTitle>Bids</DesktopTitle>
      </TitleRow>
      <DesktopPageTabsBar
        tabs={BID_TABS}
        activeKey={activeTab}
        onChange={handleTabChange}
        ariaLabel="Bid categories"
      />
      {isInitialLoading ? (
        <Loader />
      ) : hasError ? (
        <BidsErrorState onRetry={() => fetchBids(activeTab, 1)} />
      ) : bidsTableRowData.length === 0 ? (
        <BidsEmptyState />
      ) : (
        <TableWithFilters
          filters={null}
          columns={columns}
          data={bidsTableRowData}
          getRowId={(row) => `${row.id}-${row.status}`}
          isLoading={isInitialLoading}
          emptyMessage="No bids found"
          page={pageNumber}
          hasNextPage={hasNextPage}
          onPageChange={goToPage}
        />
      )}
    </DesktopPage>
  );
}
